import React, { useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import { Empty } from 'antd';

Highcharts.setOptions({ lang: { thousandsSep: ',' } });

const PieChart = ({ options, height = '400px', width = '100%', showDataLabelLines = true, showDataLabels = true, showLegend = true, size = '60%' }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    const config = {
      chart: {
        type: 'pie',
        height,
        backgroundColor: 'transparent',
        margin: [30, 30, 30, 30], // Increase margins to prevent label clipping
        spacing: [15, 15, 15, 15], // Add more spacing around chart
        ...options.chart,
      },
      title: {
        text: options.title?.text || '',
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#333',
        },
        ...options.title,
      },
      tooltip: {
        pointFormat: '<div>{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b></div>',
        ...options.tooltip,
      },
      accessibility: {
        point: {
          valueSuffix: '%'
        }
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          size: size, // Dynamic size based on prop
          dataLabels: {
            enabled: showDataLabels,
            formatter: function() {
              // Split multi-word labels onto separate lines
              const nameParts = this.point.name.split(' ');
              const formattedName = nameParts.length > 1 
                ? nameParts.join('<br/>') 
                : this.point.name;
              return `<div><b>${formattedName}</b>:<br/>${this.percentage.toFixed(1)}%</div>`;
            },
            style: {
              fontSize: '10px',
              textOutline: 'none', // Remove text outline that can cause clipping
            },
            distance: showDataLabelLines ? 20 : -25, // Increase distance for better positioning
            connectorWidth: showDataLabelLines ? 1 : 0,
            connectorColor: showDataLabelLines ? '#000000' : 'transparent',
            crookDistance: showDataLabelLines ? '70%' : 0,
            crop: false, // Prevent label cropping
            overflow: 'allow', // Allow labels to extend beyond plot area
            inside: false, // Ensure labels are outside the pie slices
            allowOverlap: false, // Prevent label overlap
            useHTML: true, // Enable HTML rendering for line breaks
          },
          showInLegend: showLegend,
          ...options.plotOptions?.pie,
        }
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '12px',
        },
        ...options.legend,
      },
      series: options.series || [],
      credits: {
        enabled: false,
      },
      ...options,
    };

    // Callers pass a new options object on every render. Update in place: a destroy under the
    // mouse leaves a pending Highcharts pointer event on a dead chart.
    const chart = chartRef.current;
    if (chart && chart.renderTo === containerRef.current) {
      chart.update(config, true, true);
      return;
    }
    chart?.destroy();
    chartRef.current = containerRef.current ? Highcharts.chart(containerRef.current, config) : null;
  }, [options, height]);

  useEffect(() => () => {
    chartRef.current?.destroy();
    chartRef.current = null;
  }, []);

  // Show a placeholder if no valid options are provided
  if (!options || !options.series || !Array.isArray(options.series) || options.series.length === 0) {
    return <Empty description="No data available" />;
  }

  return <div ref={containerRef} style={{ width, height, overflow: 'visible', position: 'relative' }} />;
};

export default PieChart; 